import {
  DocAccessType,
  DocumentMap,
  EducationSponsorshipMap,
  FamilySponsorshipMap,
  FamilySponsorType,
  IDocument,
  IDocuments,
  IDocumentsList,
  IFinance,
  IFinanceDocuments,
  IFollowUpDocuments
} from '../types/index.d';
import { hashToArray } from './helpers';
import * as types from 'types';
import { Status } from '../components/atoms/finger';
import { accountTypeLabel } from '../components/organisms/finance/BankSponsor/utils';

export function sortFn(a: { key: string; item: unknown }, b: { key: string; item: unknown }) {
  try {
    const firstRecNum = Number(a.key.substring(a.key.lastIndexOf('-') + 1));
    const secondRecNum = Number(b.key.substring(b.key.lastIndexOf('-') + 1));
    return firstRecNum >= secondRecNum ? 1 : firstRecNum <= secondRecNum ? -1 : 0;
  } catch (e) {
    return 0;
  }
}
export function getVisible(documentsHash: DocumentMap, infoOnly: boolean): IDocumentsList[] {
  const docs = hashToArray(documentsHash);
  return docs
    .filter((doc: IDocumentsList) => {
      if (doc.item.status === 'DELETED') {
        return false;
      }
      return infoOnly ? hasDocumentType(doc.item, 'infoOnly') : hasDocumentType(doc.item, 'required');
    })
    .sort(sortFn);
}

export const getDocStatus = (status: string, infoOnly = false, isLocked = false): Status => {
  if (isLocked) return Status.Locked;
  if (infoOnly) {
    return status === 'ACKNOWLEDGED'
      ? Status.Complete
      : status === 'NOT_ACKNOWLEDGED'
      ? Status.InError
      : Status.InProgress;
  }
  return ['UPLOADED_OK', 'APPROVED', 'REPLACED', 'SKIPPED'].includes(status)
    ? Status.Complete
    : status === 'UPLOADED_WITH_ERROR'
    ? Status.InError
    : Status.InProgress;
};

export const hasDocumentType = (doc: IDocument, type: string) => {
  switch (type) {
    case 'required':
      return (
        [
          'AWAITING',
          'UPLOADED_OK',
          'UPLOADED_WITH_ERROR',
          'REJECTED',
          'APPROVED',
          'REPLACED',
          'DELETED',
          'SKIPPED'
        ].includes(doc.status) && !doc.userAccess
      );
    case 'infoOnly':
      return ['INFO_ONLY', 'NOT_ACKNOWLEDGED', 'ACKNOWLEDGED'].includes(doc.status);
    case 'staffOnly':
      return doc.userAccess === DocAccessType.STAFF_ONLY;
    case 'agentOnly':
      return doc.userAccess === DocAccessType.AGENT_ONLY;
    default:
      return false;
  }
};

export const hasDocumentInError = (doc: types.IFixableDoc, type: string) =>
  doc.status === type && doc.isDocumentFixed !== true;

export const hasAnyMatchingDocs = (
  docs: IDocuments,
  type: string,
  predicate: (doc: IDocument, type: string) => boolean = hasDocumentType
): boolean => {
  let hasFoundMatch = false;

  Object.keys(docs).some((key: string) => {
    if (hasFoundMatch) {
      return true; // break out of loop
    }
    switch (key) {
      case 'cas':
      case 'academicTranscript':
      case 'academicCertificate':
      case 'parentalConsent':
      case 'tbTest':
      case 'atas':
      case 'englishTest':
      case 'englishQualification':
      case 'postStudyLetter': {
        Object.values(docs[key]).some((doc: IDocument) => {
          if (!hasFoundMatch && predicate(doc, type)) {
            hasFoundMatch = true;
            return true; // breaks out of loop
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'birthCertificate': {
        if (docs[key].certificate) {
          Object.values(docs[key].certificate!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].translation) {
          Object.values(docs[key].translation!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'passport': {
        if (docs[key].current) {
          Object.values(docs[key].current!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].renew) {
          Object.values(docs[key].renew!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'studentVisas': {
        Object.keys(docs[key]).some((studentVisasKey: string) => {
          const previousStudentVisa = docs[key][studentVisasKey];
          if (previousStudentVisa.studentVisa) {
            Object.values(previousStudentVisa.studentVisa).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (previousStudentVisa.cas) {
            Object.values(previousStudentVisa.cas).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (previousStudentVisa.biometric) {
            Object.values(previousStudentVisa.biometric).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'visaRefusals':
      case 'nonUkVisaRefusals':
      case 'otherVisas': {
        Object.keys(docs[key]).some((visaKey: string) => {
          const visaDoc = docs[key][visaKey];
          Object.values(visaDoc.visas ?? {}).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
          return false;
        });
        return hasFoundMatch;
      }
      case 'customRequest': {
        Object.keys(docs[key]).some((requestKey: string) => {
          const requestDoc = docs[key][requestKey];
          return Object.values(requestDoc!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        });
        return hasFoundMatch;
      }
      default: {
        return false;
      }
    }
  });
  return hasFoundMatch;
};

export const hasAnyMatchingFinDocs = (
  docs: IFinanceDocuments,
  type: string,
  predicate: (doc: IDocument, type: string) => boolean = hasDocumentType
): boolean => {
  let hasFoundMatch = false;

  Object.keys(docs).some((key: string) => {
    if (hasFoundMatch) {
      return true; // break out of loop
    }
    switch (key) {
      case 'educationSponsorship':
      case 'customRequest':
      case 'scholarshipSponsorship':
      case 'governmentSponsorship':
      case 'companySponsorship': {
        Object.keys(docs[key]).some((docKey: string) => {
          const sponsorshipDoc = docs[key][docKey];
          if (sponsorshipDoc) {
            return Object.values(sponsorshipDoc).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'deposit': {
        if (docs[key]) {
          return Object.values(docs[key]).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'birthCertificate': {
        if (docs[key].certificate) {
          Object.values(docs[key].certificate!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].translation) {
          Object.values(docs[key].translation!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      case 'familySponsorship': {
        Object.keys(docs[key]).some((docKey: string) => {
          const sponsorshipDoc = docs[key][docKey];
          if (sponsorshipDoc.bankStatement) {
            Object.values(sponsorshipDoc.bankStatement).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }

          if (sponsorshipDoc.bankStatementTranslation) {
            Object.values(sponsorshipDoc.bankStatementTranslation!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.bankLetter) {
            Object.values(sponsorshipDoc.bankLetter!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.parentLetter) {
            Object.values(sponsorshipDoc.parentLetter!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.verificationBankLetter) {
            Object.values(sponsorshipDoc.verificationBankLetter!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          if (sponsorshipDoc.secondaryBankStatement) {
            Object.values(sponsorshipDoc.secondaryBankStatement!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
          }
          return false;
        });
        return hasFoundMatch;
      }
      case 'evidenceOfIncome': {
        if (docs[key].curriculumVitae) {
          Object.values(docs[key].curriculumVitae!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].employmentReference) {
          Object.values(docs[key].employmentReference!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].recentEmploymentContract) {
          Object.values(docs[key].recentEmploymentContract!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        if (docs[key].recentPayslip) {
          Object.values(docs[key].recentPayslip!).some((doc: IDocument) => {
            if (!hasFoundMatch && predicate(doc, type)) {
              hasFoundMatch = true;
              return true; // breaks out of loop
            }
            return false;
          });
        }
        return hasFoundMatch;
      }
      default: {
        return false;
      }
    }
  });
  return hasFoundMatch;
};

export const hasAnyMatchingFollowUpDocs = (
  docs: IFollowUpDocuments,
  areaType: 'beforeTravel' | 'afterArrival',
  type: string,
  predicate: (doc: IDocument, type: string) => boolean = hasDocumentType
): boolean => {
  let hasFoundMatch = false;
  switch (areaType) {
    case 'beforeTravel': {
      Object.keys(docs[areaType]).some((docType: string) => {
        if (hasFoundMatch) {
          return true; // break out of loop
        }
        switch (docType) {
          case 'studentRefusalLetter':
          case 'visaVignette':
          case 'travelTicket':
          case 'immigrationStatusShareCode':
          case 'immigrationStatusPdfCopy':
          case 'visaApplicationPdfCopy': {
            Object.values(docs[areaType][docType]!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
            return hasFoundMatch;
          }
          case 'customRequest': {
            if (docs[areaType].customRequest!) {
              Object.keys(docs[areaType].customRequest!).some((requestKey: string) => {
                const requestDoc = docs[areaType].customRequest![requestKey];
                return Object.values(requestDoc!).some((doc: IDocument) => {
                  if (!hasFoundMatch && predicate(doc, type)) {
                    hasFoundMatch = true;
                    return true; // breaks out of loop
                  }
                  return false;
                });
              });
              return hasFoundMatch;
            }
            return hasFoundMatch;
          }
        }
        return hasFoundMatch;
      });
      return hasFoundMatch;
    }
    case 'afterArrival': {
      Object.keys(docs[areaType]).some((docType: string) => {
        if (hasFoundMatch) {
          return true; // break out of loop
        }

        switch (docType) {
          case 'stampedVisaVignette':
          case 'biometricResidencePermit': {
            Object.values(docs[areaType][docType]!).some((doc: IDocument) => {
              if (!hasFoundMatch && predicate(doc, type)) {
                hasFoundMatch = true;
                return true; // breaks out of loop
              }
              return false;
            });
            return hasFoundMatch;
          }
          case 'customRequest': {
            if (docs[areaType].customRequest!) {
              Object.keys(docs[areaType].customRequest!).some((requestKey: string) => {
                const requestDoc = docs[areaType].customRequest![requestKey];
                return Object.values(requestDoc).some((doc: IDocument) => {
                  if (!hasFoundMatch && predicate(doc, type)) {
                    hasFoundMatch = true;
                    return true; // breaks out of loop
                  }
                  return false;
                });
              });
              return hasFoundMatch;
            }
            return hasFoundMatch;
          }
        }
        return hasFoundMatch;
      });
      return hasFoundMatch;
    }
    default: {
      return false;
    }
  }
};

export function bankStatementName(sponsorType: FamilySponsorType) {
  switch (sponsorType) {
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit Statement Scan';
    case FamilySponsorType.STANDARD:
    case FamilySponsorType.SAVINGS:
    case FamilySponsorType.INVESTMENT:
    case FamilySponsorType.PENSION:
    default:
      return 'Bank Statement Scan';
  }
}

export function bankStatementTransName(sponsorType: FamilySponsorType) {
  switch (sponsorType) {
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit Statement Translation';
    case FamilySponsorType.STANDARD:
    case FamilySponsorType.SAVINGS:
    case FamilySponsorType.INVESTMENT:
    case FamilySponsorType.PENSION:
    default:
      return 'Bank Statement Translation';
  }
}

export function bankLetterName(sponsorType: FamilySponsorType) {
  switch (sponsorType) {
    case FamilySponsorType.TERM_DEPOSIT:
      return 'Term Deposit Letter';
    case FamilySponsorType.STANDARD:
    case FamilySponsorType.SAVINGS:
    case FamilySponsorType.INVESTMENT:
    case FamilySponsorType.PENSION:
    default:
      return 'Bank Letter';
  }
}

export function getLeadSponsorByKey(finance: IFinance): string {
  const familyType: string | undefined = Object.values(finance.familySponsorship)?.[0]?.accountType;
  const eduType: string | undefined = Object.values(finance.educationSponsorship)?.[0]?.personName;
  let type: string = (familyType ? accountTypeLabel(familyType) : '') || eduType;
  type = ['Mine', 'Joint', 'Other'].includes(type) ? 'Student' : type;
  return type;
}

// Choose which sponsor owns the Financial Birth Certificate.
export function findBirthCertSponsor(
  familySponsorship: FamilySponsorshipMap,
  educationSponsorship: EducationSponsorshipMap
): string | null {
  const findFam = hashToArray(familySponsorship).find(({ item }) => {
    return ['Mother', 'Father', 'Guardian'].includes(item.accountType);
  });
  if (findFam) {
    return findFam.key;
  }

  const findEdu = hashToArray(educationSponsorship).find(({ item }) => {
    return ['Mother', 'Father', 'Guardian'].includes(item.personName);
  });
  if (findEdu) {
    return findEdu.key;
  }

  return null;
}

export function getDocDetails(data: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  docs: any;
  sponsorType: string | null;
  sponsorKey: string | null;
  docType: string | null;
  docKey: string | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}): IDocument {
  const { docs, sponsorType, sponsorKey, docType, docKey } = data;

  let docDetails;
  if (sponsorType && sponsorKey && docType && docKey) {
    if (
      [types.IFollowUpArea.BEFORE_TRAVEL, types.IFollowUpArea.AFTER_ARRIVAL].includes(
        sponsorType as types.IFollowUpArea
      )
    ) {
      // follow-up custom docs
      docDetails = docs[sponsorType][docType][sponsorKey][docKey];
    } else if (['companySponsorship', 'governmentSponsorship'].includes(sponsorType)) {
      docDetails = docs[sponsorType][sponsorKey][docKey];
    } else {
      docDetails = docs[sponsorType][sponsorKey][docType][docKey];
    }
  } else if (sponsorType && docType === null && sponsorKey === null && docKey) {
    docDetails = docs[sponsorType][docKey];
  } else if (sponsorType && docType === null && sponsorKey && docKey) {
    docDetails = docs[sponsorType][sponsorKey][docKey];
  } else if (sponsorType === null && sponsorKey === null && docType && docKey) {
    docDetails = docs[docType][docKey];
  } else if (sponsorType && sponsorKey === null && docType && docKey) {
    docDetails = docs[sponsorType][docType][docKey];
  }

  return docDetails;
}
